import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  EyeIcon,
  SaveIcon,
  Trash2Icon,
  ChevronUpIcon,
  ChevronDownIcon,
  CalendarIcon,
  DownloadIcon,
  CheckCircleIcon,
  Settings2Icon,
  RefreshCwIcon,
  XIcon,
  TableIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Select } from '@/components/ui/select'
import { BRAND_MINT, cn } from '@/lib/utils'
import {
  getCsvExportColumnsApi,
  getCsvExportConfigsApi,
  saveCsvExportConfigApi,
  updateCsvExportConfigApi,
  deleteCsvExportConfigApi,
  generateCsvExportApi,
  type CsvExportColumn,
  type CsvColumnMapping,
  type CsvExportConfig,
  type CsvPreviewColumn,
} from '@/lib/api'

export const Route = createFileRoute('/dashboard/csv-export')({
  component: CsvExportPage,
})

// ── Group colours ──────────────────────────────────────────────────────

const GROUP_COLORS: Record<string, string> = {
  Employee: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
  Circle: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
  Organization: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800',
  'Payment Cycle': 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
  Participants: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800',
}

const GROUP_ORDER = ['Employee', 'Circle', 'Organization', 'Payment Cycle', 'Participants']

function CsvExportPage() {
  // ── State ──────────────────────────────────────────────────────────
  const [columns, setColumns] = useState<CsvExportColumn[]>([])
  const [configs, setConfigs] = useState<CsvExportConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [generating, setGenerating] = useState(false)

  // Config editor state
  const [configName, setConfigName] = useState('')
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null)
  const [columnMappings, setColumnMappings] = useState<CsvColumnMapping[]>([])
  const [includeHeaders, setIncludeHeaders] = useState(true)
  const [delimiter, setDelimiter] = useState(',')

  // Date range filters
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  // Preview state
  const [previewResult, setPreviewResult] = useState<{
    csv_content: string
    row_count: number
    filename: string
    preview_rows: Record<string, string>[]
    preview_columns: CsvPreviewColumn[]
  } | null>(null)

  // Load data
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [columnsRes, configsRes] = await Promise.all([
        getCsvExportColumnsApi(),
        getCsvExportConfigsApi(),
      ])
      setColumns(columnsRes.columns)
      setConfigs(configsRes.data)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load CSV export data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Group columns by group
  const groupedColumns = GROUP_ORDER.map((group) => ({
    group,
    columns: columns.filter((c) => c.group === group),
  })).filter((g) => g.columns.length > 0)

  // Check if a source key is already in the mapping
  const isColumnSelected = (sourceKey: string) =>
    columnMappings.some((m) => m.sourceKey === sourceKey)

  // ── Column management ──────────────────────────────────────────────

  const addColumn = (sourceKey: string) => {
    if (isColumnSelected(sourceKey)) return
    const col = columns.find((c) => c.sourceKey === sourceKey)
    if (!col) return
    setColumnMappings((prev) => [
      ...prev,
      {
        sourceKey,
        headerName: col.label,
        sortOrder: prev.length + 1,
      },
    ])
  }

  const removeColumn = (sourceKey: string) => {
    setColumnMappings((prev) => {
      const filtered = prev.filter((m) => m.sourceKey !== sourceKey)
      // Re-assign sort orders
      return filtered.map((m, i) => ({ ...m, sortOrder: i + 1 }))
    })
  }

  const updateHeaderName = (sourceKey: string, headerName: string) => {
    setColumnMappings((prev) =>
      prev.map((m) => (m.sourceKey === sourceKey ? { ...m, headerName } : m)),
    )
  }

  const moveColumn = (sourceKey: string, direction: 'up' | 'down') => {
    setColumnMappings((prev) => {
      const idx = prev.findIndex((m) => m.sourceKey === sourceKey)
      if (idx === -1) return prev
      if (direction === 'up' && idx === 0) return prev
      if (direction === 'down' && idx === prev.length - 1) return prev

      const next = [...prev]
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1
      ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
      // Re-assign sort orders
      return next.map((m, i) => ({ ...m, sortOrder: i + 1 }))
    })
  }

  // ── Config load / save / delete ────────────────────────────────────

  const loadConfig = (config: CsvExportConfig) => {
    setSelectedConfigId(config.config_id)
    setConfigName(config.config_name)
    setColumnMappings(config.column_mappings)
    setIncludeHeaders(config.include_headers)
    setDelimiter(config.delimiter || ',')
  }

  const resetEditor = () => {
    setSelectedConfigId(null)
    setConfigName('')
    setColumnMappings([])
    setIncludeHeaders(true)
    setDelimiter(',')
    setFromDate('')
    setToDate('')
    setPreviewResult(null)
  }

  const handleSaveConfig = async () => {
    if (!configName.trim()) {
      toast.error('Please enter a configuration name.')
      return
    }
    if (columnMappings.length === 0) {
      toast.error('Please add at least one column.')
      return
    }

    setSaving(true)
    try {
      const body = {
        config_name: configName.trim(),
        column_mappings: columnMappings,
        include_headers: includeHeaders,
        delimiter,
      }

      if (selectedConfigId) {
        await updateCsvExportConfigApi(selectedConfigId, body)
        toast.success('Configuration updated.')
      } else {
        const result = await saveCsvExportConfigApi(body)
        setSelectedConfigId(result.config_id)
        toast.success('Configuration saved.')
      }

      await fetchData()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save configuration.')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteConfig = async (configId: number) => {
    try {
      await deleteCsvExportConfigApi(configId)
      toast.success('Configuration deleted.')
      if (selectedConfigId === configId) {
        resetEditor()
      }
      await fetchData()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete configuration.')
    }
  }

  // ── CSV generation & preview ──────────────────────────────────────

  const handlePreviewCsv = async () => {
    if (columnMappings.length === 0) {
      toast.error('Please add at least one column to export.')
      return
    }

    setGenerating(true)
    setPreviewResult(null)
    try {
      const params: Record<string, unknown> = {
        column_mappings: columnMappings,
        include_headers: includeHeaders,
        delimiter,
        filters: {} as Record<string, string>,
      }

      if (fromDate) {
        (params.filters as Record<string, string>).from_date = fromDate
      }
      if (toDate) {
        (params.filters as Record<string, string>).to_date = toDate
      }
      if (!fromDate && !toDate) {
        delete params.filters
      }
      if (selectedConfigId) {
        params.config_id = selectedConfigId
      }

      const result = await generateCsvExportApi(params)

      if (!result.csv_content || result.row_count === 0) {
        toast.error(result.message || 'No data found matching the criteria.')
        return
      }

      setPreviewResult({
        csv_content: result.csv_content,
        row_count: result.row_count,
        filename: result.filename || 'salary-export.csv',
        preview_rows: result.preview_rows ?? [],
        preview_columns: result.preview_columns ?? columnMappings.map((m) => ({
          headerName: m.headerName,
          sourceKey: m.sourceKey,
        })),
      })

      toast.success(`Preview ready — ${result.row_count} rows found.`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate CSV.')
    } finally {
      setGenerating(false)
    }
  }

  const handleDownloadFromPreview = () => {
    if (!previewResult) return

    const blob = new Blob([previewResult.csv_content], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = previewResult.filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    toast.success(`Downloaded ${previewResult.row_count} rows.`)
  }

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-8 flex items-start justify-between">
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="mt-0.5 shrink-0"
            style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
          />
          <div>
            <h1 className="text-2xl font-semibold">Generate payrun</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Configure column mappings, reorder and rename columns, then generate a CSV
              file for your payroll platform. Saved configurations can be reused.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
        {/* ─── Left: Column configurator ─── */}
        <div className="space-y-6">
          {/* Config name + header toggle */}
          <div className="rounded-xl border border-border bg-card p-4 space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="configName">Configuration name</Label>
                <Input
                  id="configName"
                  placeholder="e.g. My Bank Payroll Export"
                  value={configName}
                  onChange={(e) => setConfigName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="delimiter">Delimiter</Label>
                <Select
                  id="delimiter"
                  value={delimiter}
                  onChange={(e) => setDelimiter(e.target.value)}
                  className="w-24"
                >
                  <option value=",">Comma (,)</option>
                  <option value=";">Semicolon (;)</option>
                  <option value="\t">Tab</option>
                  <option value="|">Pipe (|)</option>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="includeHeaders"
                checked={includeHeaders}
                onCheckedChange={(checked) => setIncludeHeaders(!!checked)}
              />
              <Label htmlFor="includeHeaders" className="text-sm font-normal cursor-pointer">
                Include header row in CSV output
              </Label>
            </div>
          </div>

          {/* Date range filter */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <CalendarIcon className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium">Date range filter</span>
              <span className="text-xs text-muted-foreground">(optional)</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="fromDate" className="text-xs">From</Label>
                <Input
                  id="fromDate"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="h-9"
                />
              </div>
              <span className="mt-6 text-xs text-muted-foreground">→</span>
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="toDate" className="text-xs">To</Label>
                <Input
                  id="toDate"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="h-9"
                />
              </div>
              {(fromDate || toDate) && (
                <button
                  type="button"
                  onClick={() => { setFromDate(''); setToDate(''); }}
                  className="mt-6 flex size-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted transition-colors"
                  aria-label="Clear date range"
                >
                  <RefreshCwIcon className="size-3.5" />
                </button>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Filter payment cycles by their due date. Leave blank to include all dates.
            </p>
          </div>

          {/* Mapped columns */}
          <div className="rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold">
                Column mappings{' '}
                <span className="text-muted-foreground font-normal">
                  ({columnMappings.length})
                </span>
              </h2>
            </div>

            {columnMappings.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-12 text-center">
                <Settings2Icon className="size-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">
                  No columns mapped yet. Select columns from the panel on the right.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {columnMappings.map((mapping, index) => (
                  <div
                    key={mapping.sourceKey}
                    className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/30 group"
                  >
                    {/* Reorder buttons */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        type="button"
                        onClick={() => moveColumn(mapping.sourceKey, 'up')}
                        disabled={index === 0}
                        className="flex size-5 items-center justify-center rounded text-muted-foreground/50 hover:text-foreground disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                        aria-label="Move up"
                      >
                        <ChevronUpIcon className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveColumn(mapping.sourceKey, 'down')}
                        disabled={index === columnMappings.length - 1}
                        className="flex size-5 items-center justify-center rounded text-muted-foreground/50 hover:text-foreground disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                        aria-label="Move down"
                      >
                        <ChevronDownIcon className="size-3.5" />
                      </button>
                    </div>

                    {/* Order badge */}
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-mono font-semibold text-muted-foreground">
                      {index + 1}
                    </span>

                    {/* Source key (readonly) */}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-mono text-muted-foreground">
                        {mapping.sourceKey}
                      </p>
                      <Input
                        value={mapping.headerName}
                        onChange={(e) => updateHeaderName(mapping.sourceKey, e.target.value)}
                        className="mt-0.5 h-8 text-sm"
                        placeholder="Column header name"
                      />
                    </div>

                    {/* Remove button */}
                    <button
                      type="button"
                      onClick={() => removeColumn(mapping.sourceKey)}
                      className="flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground/50 opacity-0 group-hover:opacity-100 hover:text-destructive hover:bg-destructive/10 transition-all"
                      aria-label={`Remove ${mapping.headerName}`}
                    >
                      <Trash2Icon className="size-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handlePreviewCsv}
              disabled={columnMappings.length === 0 || generating}
              className="gap-1.5"
            >
              {generating ? (
                <>
                  <span className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Generating…
                </>
              ) : (
                <>
                  <EyeIcon className="size-4" />
                  Preview CSV
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleSaveConfig}
              disabled={saving || columnMappings.length === 0 || !configName.trim()}
              className="gap-1.5"
            >
              <SaveIcon className="size-4" />
              {saving ? 'Saving…' : selectedConfigId ? 'Update config' : 'Save config'}
            </Button>
            {(selectedConfigId || configName || columnMappings.length > 0) && (
              <Button variant="ghost" onClick={resetEditor} className="gap-1.5 text-muted-foreground">
                <RefreshCwIcon className="size-4" />
                New config
              </Button>
            )}
          </div>

          {/* ── Preview panel ── */}
          {previewResult && (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              {/* Preview header */}
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                  <TableIcon className="size-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">Preview</span>
                  <span className="text-xs text-muted-foreground">
                    Showing {Math.min(previewResult.preview_rows.length, 5)} of{' '}
                    {previewResult.row_count} rows
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={handleDownloadFromPreview}
                    className="gap-1.5"
                  >
                    <DownloadIcon className="size-3.5" />
                    Download full CSV
                  </Button>
                  <button
                    type="button"
                    onClick={() => setPreviewResult(null)}
                    className="flex size-8 items-center justify-center rounded-lg text-muted-foreground/50 hover:text-foreground hover:bg-muted transition-colors"
                    aria-label="Close preview"
                  >
                    <XIcon className="size-4" />
                  </button>
                </div>
              </div>

              {/* Preview table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="sticky left-0 z-10 w-10 bg-card px-2 py-2 text-center text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
                        #
                      </th>
                      {previewResult.preview_columns.map((col) => (
                        <th
                          key={col.sourceKey}
                          className="whitespace-nowrap px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                        >
                          {col.headerName}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {previewResult.preview_rows.map((row, rowIdx) => (
                      <tr
                        key={rowIdx}
                        className="transition-colors hover:bg-muted/20"
                      >
                        <td className="sticky left-0 z-10 bg-card px-2 py-2 text-center font-mono text-[10px] text-muted-foreground">
                          {rowIdx + 1}
                        </td>
                        {previewResult.preview_columns.map((col) => (
                          <td
                            key={col.sourceKey}
                            className="max-w-[200px] truncate px-3 py-2 font-mono text-foreground/80"
                            title={row[col.headerName]}
                          >
                            {row[col.headerName] || (
                              <span className="italic text-muted-foreground/40">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Preview footer */}
              <div className="flex items-center justify-between border-t border-border px-4 py-2.5">
                <p className="text-xs text-muted-foreground">
                  {previewResult.row_count === 1
                    ? '1 row'
                    : previewResult.row_count <= 5
                      ? `${previewResult.row_count} rows`
                      : `Showing first 5 of ${previewResult.row_count} rows`}
                </p>
                <p className="text-[10px] font-mono text-muted-foreground/50">
                  {previewResult.filename}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ─── Right: Available columns + Saved configs ─── */}
        <div className="space-y-6">
          {/* Saved configurations */}
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold">Saved configs</h2>
            </div>
            <div className="divide-y divide-border max-h-48 overflow-y-auto">
              {configs.length === 0 ? (
                <p className="px-4 py-6 text-center text-xs text-muted-foreground">
                  No saved configurations yet.
                </p>
              ) : (
                configs.map((cfg) => (
                  <div
                    key={cfg.config_id}
                    className={cn(
                      'flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-muted/30 cursor-pointer',
                      selectedConfigId === cfg.config_id && 'bg-muted/50',
                    )}
                    onClick={() => loadConfig(cfg)}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{cfg.config_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {cfg.column_mappings.length} columns ·{' '}
                        {cfg.include_headers ? 'headers' : 'no headers'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteConfig(cfg.config_id)
                      }}
                      className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
                      aria-label={`Delete ${cfg.config_name}`}
                    >
                      <Trash2Icon className="size-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Available columns */}
          <div className="rounded-xl border border-border bg-card">
            <div className="border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold">Available columns</h2>
            </div>
            <div className="max-h-[500px] overflow-y-auto p-3 space-y-3">
              {groupedColumns.map(({ group, columns: groupCols }) => (
                <div key={group}>
                  <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {group}
                  </p>
                  <div className="space-y-1">
                    {groupCols.map((col) => {
                      const selected = isColumnSelected(col.sourceKey)
                      return (
                        <button
                          key={col.sourceKey}
                          type="button"
                          onClick={() => {
                            if (selected) {
                              removeColumn(col.sourceKey)
                            } else {
                              addColumn(col.sourceKey)
                            }
                          }}
                          disabled={loading}
                          className={cn(
                            'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-all',
                            selected
                              ? 'bg-muted/50 border-primary/30'
                              : 'border-transparent hover:bg-muted/30 hover:border-border',
                          )}
                        >
                          <div
                            className={cn(
                              'flex size-4 shrink-0 items-center justify-center rounded border transition-colors',
                              selected
                                ? 'bg-primary border-primary text-primary-foreground'
                                : 'border-muted-foreground/30',
                            )}
                          >
                            {selected && <CheckCircleIcon className="size-3" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium text-foreground">
                              {col.label}
                            </p>
                            <p className="text-[10px] text-muted-foreground leading-normal">
                              {col.description}
                            </p>
                          </div>
                          <span
                            className={cn(
                              'shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider',
                              GROUP_COLORS[group] ?? 'bg-muted text-muted-foreground',
                            )}
                          >
                            {group === 'Payment Cycle' ? 'Cycle' : group}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
